import { useCallback, useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  accordNombre,
  denombrer,
  nommerJoursDePratique,
  pluriel,
  GENRES,
  GENRE_LIBELLE,
  MATIERES,
  NIVEAUX_SCOLAIRES,
  bornesDateNaissance,
  dateNaissanceValide,
  type AccountRole,
  type CompteListeItemDto,
  type ConnexionDto,
  type EleveAdminDto,
  type Genre,
  type ListeAffectationsResponse,
  type ListeComptesResponse,
  type ListeConnexionsResponse,
  type ListeElevesAdminResponse,
  type Matiere,
  type NiveauScolaire,
  type UsageIaResponse,
} from '@silex/shared';

import { api, ApiError } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { Badge } from '../components/Badge';
import { EmptyState } from '../components/EmptyState';
import {
  accountRoleLabel,
  activiteEleveBadge,
  connexionBadge,
  creditActionLabel,
  facturationResume,
  formatDate,
  formatDateHeure,
  formatNombre,
  formatUsd,
  formatUsdPrecis,
  isoJour,
  niveauScolaireLabel,
  part,
} from '../lib/admin-format';
import { MANUEL_VIVANT } from '../lib/manuel-vivant';
import { matiereLabel } from '../lib/suivi-format';

import { ReglageManuelVivant } from './ReglageManuelVivant';

/* ========================================================================== */
/* Chargement asynchrone                                                      */
/* ========================================================================== */

type Resource<T> =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly data: T }
  | { readonly status: 'error'; readonly message: string };

function messageErreur(err: unknown): string {
  if (err instanceof ApiError) {
    // 403 : la garde client n'est qu'un confort, l'API tranche (requireRole).
    if (err.status === 403) return "Votre compte n'a pas les droits d'administration.";
    return err.message;
  }
  return 'Erreur de chargement.';
}

/**
 * Charge `loader()` au montage et à chaque changement d'identité de la
 * fonction. `reload` force un rechargement après une écriture.
 */
function useAsync<T>(loader: () => Promise<T>): Resource<T> & { reload: () => void } {
  const [state, setState] = useState<Resource<T>>({ status: 'loading' });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    loader()
      .then((data) => {
        if (!cancelled) setState({ status: 'ready', data });
      })
      .catch((err: unknown) => {
        if (!cancelled) setState({ status: 'error', message: messageErreur(err) });
      });
    return () => {
      cancelled = true;
    };
  }, [loader, tick]);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { ...state, reload };
}

/* ========================================================================== */
/* Fragments partagés                                                         */
/* ========================================================================== */

function ErreurChargement({ message }: { message: string }) {
  return (
    <div className="callout callout--rare" role="alert">
      <strong>Chargement impossible.</strong> {message}
    </div>
  );
}

function Chargement() {
  return <p className="suivi-note">Chargement…</p>;
}

/** Enveloppe commune : erreur, chargement, puis rendu des données. */
function Async<T>({
  resource,
  children,
}: {
  resource: Resource<T>;
  children: (data: T) => ReactNode;
}) {
  if (resource.status === 'loading') return <Chargement />;
  if (resource.status === 'error') return <ErreurChargement message={resource.message} />;
  return <>{children(resource.data)}</>;
}

/** Bouton « charger la suite » — visible seulement si l'API a rendu un curseur. */
function PlusDeResultats({
  cursor,
  chargement,
  onClick,
}: {
  cursor: string | null;
  chargement: boolean;
  onClick: () => void;
}) {
  if (!cursor) return null;
  return (
    <div className="row" style={{ marginTop: 'var(--espace-4)' }}>
      <button
        type="button"
        className="btn btn--ghost btn--sm"
        disabled={chargement}
        onClick={onClick}
      >
        {chargement ? 'Chargement…' : 'Charger la suite'}
      </button>
    </div>
  );
}

/**
 * Pagination par curseur : conserve les pages déjà chargées et concatène la
 * suivante. `extract` isole la liste et le curseur de la réponse.
 */
function usePagination<TResponse, TItem>(
  premierePage: Resource<TResponse>,
  extract: (r: TResponse) => { items: readonly TItem[]; nextCursor: string | null },
  charger: (cursor: string) => Promise<TResponse>,
) {
  const [suite, setSuite] = useState<readonly TItem[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  // Une nouvelle première page (filtre changé) réinitialise les pages suivantes.
  const base = premierePage.status === 'ready' ? extract(premierePage.data) : null;
  const baseCursor = base?.nextCursor ?? null;
  useEffect(() => {
    setSuite([]);
    setCursor(baseCursor);
    setErreur(null);
  }, [baseCursor]);

  const plus = useCallback(async () => {
    if (!cursor) return;
    setChargement(true);
    setErreur(null);
    try {
      const res = await charger(cursor);
      const { items, nextCursor } = extract(res);
      setSuite((prev) => [...prev, ...items]);
      setCursor(nextCursor);
    } catch (err) {
      setErreur(messageErreur(err));
    } finally {
      setChargement(false);
    }
  }, [cursor, charger, extract]);

  const items = base ? [...base.items, ...suite] : [];
  return { items, cursor, chargement, erreur, plus };
}

/* ========================================================================== */
/* Onglet — Consommation IA                                                   */
/* ========================================================================== */

const FENETRES = [
  { jours: 7, label: '7 jours' },
  { jours: 30, label: '30 jours' },
  { jours: 90, label: '90 jours' },
] as const;

function ConsommationTab() {
  const [jours, setJours] = useState<number>(30);

  const loader = useCallback(() => {
    const depuis = new Date(Date.now() - jours * 86_400_000);
    return api.getUsageIa({ depuis: isoJour(depuis), groupBy: 'jour' });
  }, [jours]);
  const usage = useAsync<UsageIaResponse>(loader);

  return (
    <div className="suivi-stack">
      <div className="suivi-section-header">
        <h2>Consommation IA</h2>
        <div className="segmented" role="group" aria-label="Fenêtre d'observation">
          {FENETRES.map((f) => (
            <button
              key={f.jours}
              type="button"
              className={jours === f.jours ? 'segmented__btn is-active' : 'segmented__btn'}
              aria-pressed={jours === f.jours}
              onClick={() => setJours(f.jours)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <Async resource={usage}>
        {(data) => (
          <>
            <div className="kpi-row">
              <div className="metric-card">
                <span className="data-xl">{formatNombre(data.totaux.nbAppels)}</span>
                <span className="data-xl-lbl">appels IA</span>
              </div>
              <div className="metric-card">
                <span className="data-xl">{formatUsd(data.totaux.coutTotalUsd)}</span>
                <span className="data-xl-lbl">coût total</span>
              </div>
              <div className="metric-card">
                <span className="data-xl">{formatUsdPrecis(data.totaux.coutMoyenUsd)}</span>
                <span className="data-xl-lbl">coût moyen par appel</span>
              </div>
            </div>

            <TendanceCard parJour={data.parJour} />
            <RepartitionCard parAction={data.parAction} totalAppels={data.totaux.nbAppels} />

            <p className="note-line">
              Fenêtre observée : du {formatDate(data.depuis)} au {formatDate(data.jusqua)}.
            </p>
          </>
        )}
      </Async>
    </div>
  );
}

/** Tendance quotidienne — histogramme, hauteur relative au maximum de la fenêtre. */
function TendanceCard({ parJour }: { parJour: UsageIaResponse['parJour'] }) {
  if (parJour.length === 0) {
    return (
      <section className="card">
        <h3>Tendance quotidienne</h3>
        <p className="suivi-note">Aucun appel IA sur la fenêtre observée.</p>
      </section>
    );
  }

  const max = Math.max(...parJour.map((j) => j.nbAppels), 1);
  const premier = parJour[0];
  const dernier = parJour[parJour.length - 1];

  return (
    <section className="card">
      <h3>Tendance quotidienne</h3>
      <p className="card-sub">
        Nombre d'appels IA par jour · survolez une barre, ou dépliez le tableau, pour le détail.
      </p>
      {/* LOT 3.1 (CON-ADM-IA-03) — le graphe reste un `role="img"` décoratif :
          ses 31 barres ne sont ni focusables ni nommables une par une sans
          transformer une figure en 31 arrêts de tabulation, ce qui serait pire
          que le défaut. L'invariant demande « une alternative accessible
          (tableau, description) », et c'est le TABLEAU qui la donne : les 31
          valeurs quotidiennes existaient jusqu'ici « pour qui tient une
          souris » — l'infobulle native `title` ne se déclenche qu'au survol.
          Le tableau les rend lisibles au clavier, au lecteur d'écran, et
          copiables. `aria-hidden` sur la figure évite de faire annoncer deux
          fois la même donnée. */}
      <div
        className="trend"
        role="img"
        aria-hidden="true"
        aria-label={`Appels IA par jour, maximum ${max}`}
      >
        {parJour.map((jour) => (
          <span key={jour.date} className="trend__col">
            <span
              className="trend__bar"
              style={{ height: `${Math.max(2, Math.round((jour.nbAppels / max) * 100))}%` }}
              title={`${jour.date} — ${formatNombre(jour.nbAppels)} ${accordNombre(jour.nbAppels, 'appel')} · ${formatUsd(jour.coutUsd)}`}
            />
          </span>
        ))}
      </div>
      <div className="trend__axis">
        <span>{premier?.date}</span>
        <span>{dernier?.date}</span>
      </div>
      <details className="trend__table">
        <summary>Voir les {denombrer(parJour.length, 'valeur')} en tableau</summary>
        <table className="data-table">
          <caption className="sr-only">Appels IA et coût par jour, sur la fenêtre observée</caption>
          <thead>
            <tr>
              <th scope="col">Jour</th>
              <th scope="col">Appels</th>
              <th scope="col">Coût</th>
            </tr>
          </thead>
          <tbody>
            {parJour.map((jour) => (
              <tr key={jour.date}>
                <th scope="row">{jour.date}</th>
                <td>{formatNombre(jour.nbAppels)}</td>
                <td>{formatUsd(jour.coutUsd)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </section>
  );
}

/** Répartition par action facturable — barre de proportion + coûts. */
function RepartitionCard({
  parAction,
  totalAppels,
}: {
  parAction: UsageIaResponse['parAction'];
  totalAppels: number;
}) {
  if (parAction.length === 0) {
    return (
      <section className="card">
        <h3>Répartition par action</h3>
        <p className="suivi-note">Aucune action facturable sur la fenêtre observée.</p>
      </section>
    );
  }

  // Décroissant par coût : la ligne la plus chère se lit en premier.
  const lignes = [...parAction].sort((a, b) => b.coutUsd - a.coutUsd);

  return (
    <section className="card">
      <h3>Répartition par action</h3>
      <p className="card-sub">Triée par coût décroissant sur la fenêtre observée.</p>
      <div className="repartition">
        {lignes.map((a) => (
          <div key={a.action} className="repartition__row">
            <span className="repartition__name">{creditActionLabel(a.action)}</span>
            <span className="progress" aria-hidden="true">
              <i style={{ width: `${part(a.nbAppels, totalAppels)}%` }} />
            </span>
            <span className="repartition__val">{formatUsd(a.coutUsd)}</span>
            <span className="repartition__sub">{formatNombre(a.nbAppels)} app.</span>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ========================================================================== */
/* Onglet — Comptes                                                           */
/* ========================================================================== */

const FILTRES_ROLE: readonly { readonly value: AccountRole | 'tous'; readonly label: string }[] = [
  { value: 'tous', label: 'Tous' },
  { value: 'tuteur', label: 'Tuteurs' },
  { value: 'parent', label: 'Parents' },
  { value: 'admin', label: 'Admins' },
];

/**
 * AB#296 — récupération du double facteur d'un compte (téléphone perdu,
 * application réinstallée). Jamais sur son propre compte : l'API le refuse,
 * et le bouton ne s'affiche pas. L'action est journalisée côté API.
 */
function ReinitialiserMfa({ compte }: { compte: CompteListeItemDto }) {
  const [etat, setEtat] = useState<'repos' | 'en_cours' | 'fait'>('repos');
  const [erreur, setErreur] = useState<string | null>(null);

  async function reinitialiser() {
    const ok = window.confirm(
      `Réinitialiser la double authentification de ${compte.email} ?\n\n` +
        'Ses sessions ouvertes seront fermées. À sa prochaine connexion, il devra enrôler ' +
        'à nouveau son application d’authentification.',
    );
    if (!ok) return;
    setEtat('en_cours');
    setErreur(null);
    try {
      await api.reinitialiserMfa(compte.id);
      setEtat('fait');
    } catch (err: unknown) {
      setErreur(messageErreur(err));
      setEtat('repos');
    }
  }

  if (etat === 'fait') return <span className="cell-sub">Réinitialisée</span>;
  return (
    <>
      <button
        type="button"
        className="btn btn--ghost btn--sm"
        disabled={etat === 'en_cours'}
        onClick={() => void reinitialiser()}
      >
        Réinitialiser la double authentification
      </button>
      {erreur ? (
        <div className="cell-sub" role="alert">
          {erreur}
        </div>
      ) : null}
    </>
  );
}

/**
 * « Afficher les comptes de test » — décision du fondateur du 15 octobre 2026
 * (D-7). Éteint par défaut : la liste ne montre que les vrais comptes.
 */
export function InterrupteurComptesDeTest({
  actif,
  onChange,
}: {
  readonly actif: boolean;
  readonly onChange: (v: boolean) => void;
}) {
  return (
    <label className="chip-filter" data-testid="interrupteur-comptes-de-test">
      <input type="checkbox" checked={actif} onChange={(e) => onChange(e.target.checked)} />{' '}
      Afficher les comptes de test
    </label>
  );
}

/**
 * L'abonnement du compte, en lecture seule (fondateur, 23 oct. 2026) : formule,
 * état, présence chez le prestataire et dernier paiement — lus en base. Un
 * serveur d'avant n'envoie pas le champ : la cellule reste vide.
 */
export function CelluleFacturation({ compte }: { readonly compte: CompteListeItemDto }) {
  const f = compte.facturation;
  if (f === undefined) return null;
  if (f === null) {
    return compte.role === 'parent' || compte.role === 'eleve' ? (
      <span className="cell-sub">Aucun abonnement</span>
    ) : null;
  }
  const r = facturationResume(f);
  return (
    <div data-testid="facturation-compte">
      <Badge tone={r.tone} label={r.label} />
      <div style={{ marginTop: 4 }}>{r.formule}</div>
      <div className="cell-sub">{r.paiement}</div>
      <div className="cell-sub">
        {f.abonnementPrestataire ? 'Abonnement chez Stripe' : 'Rien chez Stripe'}
      </div>
    </div>
  );
}

function ComptesTab({ avecReglage = true }: { readonly avecReglage?: boolean }) {
  const { user } = useAuth();
  const [role, setRole] = useState<AccountRole | 'tous'>('tous');
  const [saisie, setSaisie] = useState('');
  const [recherche, setRecherche] = useState('');
  /* D-7 (15 oct. 2026) : les comptes de test sont masqués PAR LE SERVEUR (la
     liste est paginée : un filtre d'écran rendrait des pages trouées). */
  const [avecTests, setAvecTests] = useState(false);

  const loader = useCallback(
    () =>
      api.listComptes({
        ...(role === 'tous' ? {} : { role }),
        ...(recherche ? { q: recherche } : {}),
        ...(avecTests ? { inclureTests: true } : {}),
      }),
    [role, recherche, avecTests],
  );
  const comptes = useAsync<ListeComptesResponse>(loader);

  const extract = useCallback(
    (r: ListeComptesResponse) => ({ items: r.comptes, nextCursor: r.nextCursor }),
    [],
  );
  const chargerSuite = useCallback(
    (cursor: string) =>
      api.listComptes({
        ...(role === 'tous' ? {} : { role }),
        ...(recherche ? { q: recherche } : {}),
        ...(avecTests ? { inclureTests: true } : {}),
        cursor,
      }),
    [role, recherche, avecTests],
  );
  const page = usePagination<ListeComptesResponse, CompteListeItemDto>(
    comptes,
    extract,
    chargerSuite,
  );

  return (
    <div className="suivi-stack">
      <div className="suivi-section-header">
        <h2>Comptes</h2>
        <form
          className="row"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            setRecherche(saisie.trim());
          }}
        >
          <span className="field field--inline" style={{ width: 220 }}>
            <input
              className="control"
              type="search"
              aria-label="Rechercher un compte"
              placeholder="Nom ou courriel…"
              value={saisie}
              onChange={(e) => setSaisie(e.target.value)}
              style={{ minHeight: 40 }}
            />
          </span>
          <button type="submit" className="btn btn--ghost btn--sm">
            Rechercher
          </button>
        </form>
      </div>

      <div className="filters">
        {FILTRES_ROLE.map((f) => (
          <button
            key={f.value}
            type="button"
            className={role === f.value ? 'chip-filter is-on' : 'chip-filter'}
            aria-pressed={role === f.value}
            onClick={() => setRole(f.value)}
          >
            {f.label}
          </button>
        ))}
        <InterrupteurComptesDeTest actif={avecTests} onChange={setAvecTests} />
      </div>

      <Async resource={comptes}>
        {() =>
          page.items.length === 0 ? (
            <EmptyState title="Aucun compte">
              Aucun compte ne correspond à ce filtre. Les comptes créés depuis l'onglet « Création »
              apparaissent ici immédiatement.
            </EmptyState>
          ) : (
            <>
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th scope="col">Compte</th>
                      <th scope="col">Rôle</th>
                      <th scope="col">Créé le</th>
                      <th scope="col">Dernière connexion</th>
                      <th scope="col">Abonnement</th>
                      <th scope="col">Double authentification</th>
                    </tr>
                  </thead>
                  <tbody>
                    {page.items.map((c) => {
                      const badge = connexionBadge(c.lastLoginAt);
                      return (
                        <tr key={c.id}>
                          <td>
                            <div className="row">
                              <span className="avatar-sq avatar-sq--sm" aria-hidden="true">
                                {(c.prenom || c.email).charAt(0).toUpperCase()}
                              </span>
                              <div>
                                <div style={{ fontWeight: 600 }}>
                                  {c.prenom} {c.nom}
                                </div>
                                <div className="cell-sub">{c.email}</div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className="tag">{accountRoleLabel(c.role)}</span>
                            {c.compteDeTest ? (
                              <>
                                {' '}
                                <span className="tag" data-testid="compte-de-test">
                                  Test
                                </span>
                              </>
                            ) : null}
                          </td>
                          <td className="data">{formatDate(c.createdAt)}</td>
                          <td>
                            <Badge tone={badge.tone} label={badge.label} />
                          </td>
                          <td>
                            <CelluleFacturation compte={c} />
                          </td>
                          <td>
                            {c.role !== 'eleve' && c.id !== user?.id ? (
                              <ReinitialiserMfa compte={c} />
                            ) : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {page.erreur ? <ErreurChargement message={page.erreur} /> : null}
              <PlusDeResultats
                cursor={page.cursor}
                chargement={page.chargement}
                onClick={() => void page.plus()}
              />
            </>
          )
        }
      </Async>

      {/* Chantier « manuel vivant » (MV-SOC-01) — le réglage de la capacité, par
          foyer ou par organisation. Rendu seulement quand le build embarque le
          chantier : drapeau de build éteint, cet onglet est celui d'avant. */}
      {MANUEL_VIVANT && avecReglage ? <ReglageManuelVivant /> : null}
    </div>
  );
}

/* ========================================================================== */
/* Onglet — Connexions                                                        */
/* ========================================================================== */

function ConnexionsTab() {
  const [jours, setJours] = useState<number>(30);

  const loader = useCallback(
    () => api.listConnexions({ depuis: isoJour(new Date(Date.now() - jours * 86_400_000)) }),
    [jours],
  );
  const connexions = useAsync<ListeConnexionsResponse>(loader);

  const extract = useCallback(
    (r: ListeConnexionsResponse) => ({ items: r.connexions, nextCursor: r.nextCursor }),
    [],
  );
  const chargerSuite = useCallback(
    (cursor: string) =>
      api.listConnexions({
        depuis: isoJour(new Date(Date.now() - jours * 86_400_000)),
        cursor,
      }),
    [jours],
  );
  const page = usePagination<ListeConnexionsResponse, ConnexionDto>(
    connexions,
    extract,
    chargerSuite,
  );

  return (
    <div className="suivi-stack">
      <div className="suivi-section-header">
        <h2>Connexions</h2>
        <div className="segmented" role="group" aria-label="Fenêtre d'observation">
          {FENETRES.map((f) => (
            <button
              key={f.jours}
              type="button"
              className={jours === f.jours ? 'segmented__btn is-active' : 'segmented__btn'}
              aria-pressed={jours === f.jours}
              onClick={() => setJours(f.jours)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <Async resource={connexions}>
        {() =>
          page.items.length === 0 ? (
            <EmptyState title="Aucune connexion sur la période">
              Aucun compte ni sous-profil élève ne s'est connecté sur la fenêtre observée.
              Élargissez la fenêtre pour remonter plus loin.
            </EmptyState>
          ) : (
            <>
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th scope="col">Qui</th>
                      <th scope="col">Type</th>
                      <th scope="col">Rôle</th>
                      <th scope="col">Dernière connexion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {page.items.map((c) => (
                      <tr key={`${c.type}-${c.id}`}>
                        <td>
                          <div className="row">
                            <span className="avatar-sq avatar-sq--sm" aria-hidden="true">
                              {(c.prenom ?? c.email ?? '?').charAt(0).toUpperCase()}
                            </span>
                            <div>
                              <div style={{ fontWeight: 600 }}>{c.prenom ?? '—'}</div>
                              {/* Un sous-profil élève n'a pas de PII de contact (principe produit). */}
                              <div className="cell-sub">{c.email ?? 'Sous-profil élève'}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className={c.type === 'eleve' ? 'tag tag--acc' : 'tag'}>
                            {c.type === 'eleve' ? 'Élève' : 'Compte'}
                          </span>
                        </td>
                        <td>{c.role ? accountRoleLabel(c.role) : '—'}</td>
                        <td className="data">{formatDateHeure(c.lastLoginAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {page.erreur ? <ErreurChargement message={page.erreur} /> : null}
              <PlusDeResultats
                cursor={page.cursor}
                chargement={page.chargement}
                onClick={() => void page.plus()}
              />
            </>
          )
        }
      </Async>
    </div>
  );
}

/* ========================================================================== */
/* Onglet — Élèves                                                            */
/* ========================================================================== */

function ElevesTab() {
  const loader = useCallback(() => api.listElevesAdmin(), []);
  const eleves = useAsync<ListeElevesAdminResponse>(loader);

  const extract = useCallback(
    (r: ListeElevesAdminResponse) => ({ items: r.eleves, nextCursor: r.nextCursor }),
    [],
  );
  const chargerSuite = useCallback((cursor: string) => api.listElevesAdmin(cursor), []);
  const page = usePagination<ListeElevesAdminResponse, EleveAdminDto>(
    eleves,
    extract,
    chargerSuite,
  );

  /* ─── AB#632 — CRÉDITER UN FOYER DE TEST ───────────────────────────────────
   *
   * Le geste est destiné aux démonstrations : un testeur embarque un élève et
   * doit pouvoir faire tourner le produit sans payer.
   *
   * `enCours` porte l'identifiant de l'ÉLÈVE et non un booléen : deux clics sur
   * deux lignes différentes ne doivent pas se désactiver l'un l'autre, et la
   * ligne qui travaille doit être celle qui le montre. */
  const [enCours, setEnCours] = useState<string | null>(null);
  const [erreurOctroi, setErreurOctroi] = useState<string | null>(null);
  const [succesOctroi, setSuccesOctroi] = useState<string | null>(null);

  async function crediter(e: EleveAdminDto) {
    /* La confirmation nomme le PARENT, parce que c'est lui qui est crédité — et
       elle dit « foyer » pour que personne ne croie créditer un seul enfant. */
    const ok = window.confirm(
      `Créditer 100 000 crédits au foyer de ${e.prenom} ?\n\n` +
        `Le portefeuille appartient au titulaire du compte (${e.parentEmail}) : ` +
        `tous les élèves de ce foyer en bénéficieront.\n\n` +
        `Le solde est POSÉ à 100 000, non additionné : répéter ce geste ne double rien.`,
    );
    if (!ok) return;

    setEnCours(e.id);
    setErreurOctroi(null);
    setSuccesOctroi(null);
    try {
      const r = await api.accorderQuota(e.parentId, 'abonnement');
      /* `echec` n'est PAS un succès à moitié : les crédits sont posés et le mur
         refusera quand même. Le taire reproduirait le défaut du 22 septembre,
         où une élève VOYAIT ses crédits et se faisait refuser chaque appel. */
      if (r.acces === 'echec') {
        setErreurOctroi(
          `Les ${formatNombre(r.credits)} crédits sont posés sur le foyer de ${e.prenom}, ` +
            `mais le droit d'accès n'a PAS pu être ouvert : les appels seront refusés. ` +
            `À reprendre, ou à instruire dans les journaux.`,
        );
        return;
      }
      const suite =
        r.acces === 'ouvert'
          ? " Un abonnement d'essai a été ouvert pour donner l'accès."
          : r.acces === 'reouvert'
            ? " L'accès était fermé ; il a été rouvert en essai."
            : " L'accès était déjà actif.";
      setSuccesOctroi(
        `${formatNombre(r.credits)} crédits crédités au foyer de ${e.prenom}.${suite} ` +
          `Le montant apparaît dans le portail parent et dans celui de l'élève au prochain ` +
          `affichage de leur page d'abonnement.`,
      );
    } catch (err: unknown) {
      setErreurOctroi(messageErreur(err));
    } finally {
      setEnCours(null);
    }
  }

  return (
    <div className="suivi-stack">
      <div className="suivi-section-header">
        <h2>Utilisation par élève</h2>
      </div>

      <Async resource={eleves}>
        {() =>
          page.items.length === 0 ? (
            <EmptyState title="Aucun élève">
              Aucun profil élève n'existe encore. Créez un parent puis un enfant depuis l'onglet «
              Création ».
            </EmptyState>
          ) : (
            <>
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th scope="col">Élève</th>
                      <th scope="col">Parent</th>
                      <th scope="col">Dose (semaine)</th>
                      <th scope="col">Savoirs observés</th>
                      <th scope="col">Activité</th>
                      <th scope="col">Crédits de test</th>
                    </tr>
                  </thead>
                  <tbody>
                    {page.items.map((e) => {
                      const badge = activiteEleveBadge(e.dose.nbTentatives);
                      const {
                        nbMesuresMaitrisees: acquises,
                        nbSavoirsObserves: savoirs,
                        nbMesures: mesures,
                      } = e.progressionSynthese;
                      return (
                        <tr key={e.id}>
                          <td>
                            <div className="row">
                              <span className="avatar-sq avatar-sq--sm" aria-hidden="true">
                                {e.prenom.charAt(0).toUpperCase()}
                              </span>
                              <div>
                                <div style={{ fontWeight: 600 }}>{e.prenom}</div>
                                <div className="cell-sub">
                                  {niveauScolaireLabel(e.niveauScolaire)}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="cell-sub">{e.parentEmail}</td>
                          <td>
                            <span className="data">
                              {formatNombre(e.dose.nbTentatives)} exercices
                            </span>
                            {/* LOT 1.3 — « 4 jour(s) actif(s) » ici quand la
                                fiche parent disait « cinq » et l'écran de
                                l'élève « deux », le même jour, sur la même
                                enfant (BD-03). Un seul fait, un seul nom. */}
                            <div className="cell-sub">
                              {nommerJoursDePratique(e.dose.nbJoursActifs)}
                            </div>
                          </td>
                          <td>
                            {/* LOT 3.5 (CON-ADM-ELV-01, BLOQUANT) — cette cellule
                                rendait « 1/9 », « 3/16 », avec une barre remplie
                                à `part(acquis, vus)` pour cent. C'est-à-dire
                                EXACTEMENT le niveau agrégé par élève que
                                l'invariant n° 1 proscrit, et que la console
                                tuteur déclare interdit en toutes lettres deux
                                écrans plus loin. Le commentaire qui se trouvait
                                ici affirmait le contraire de ce que le code
                                faisait.

                                Deux faits DÉNOMBRÉS, côte à côte, jamais
                                divisés l'un par l'autre : le compte des savoirs
                                observés, et celui des savoirs qu'un humain a
                                confirmés maîtrisés. Aucune barre — une barre est
                                une proportion, quel que soit le libellé posé à
                                côté. */}
                            {/* LOT 1.4 (BD-04) — « 9 savoirs » comptait des
                                LIGNES (savoir, dimension). Un savoir visuel dont
                                le calcul est acquis et la lecture en découverte
                                y devenait un « savoir maîtrisé » : le verdict de
                                savoir que l'invariant interdit, produit par un
                                libellé. Trois compteurs nommés, jamais
                                divisés. */}
                            <span className="data">{denombrer(savoirs, 'savoir')}</span>
                            <div className="cell-sub">
                              {denombrer(mesures, 'mesure')}, dont{' '}
                              {denombrer(acquises, 'confirmée')} {pluriel(acquises, 'maîtrisée')}
                            </div>
                          </td>
                          <td>
                            <Badge tone={badge.tone} label={badge.label} />
                          </td>
                          <td>
                            <button
                              type="button"
                              className="btn btn--ghost btn--sm"
                              disabled={enCours === e.id}
                              onClick={() => void crediter(e)}
                              title={`Pose 100 000 crédits sur le foyer de ${e.parentEmail}`}
                            >
                              {enCours === e.id ? 'Octroi…' : '+ 100 000 crédits'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {page.erreur ? <ErreurChargement message={page.erreur} /> : null}
              {erreurOctroi ? <ErreurChargement message={erreurOctroi} /> : null}
              {succesOctroi ? (
                <p className="suivi-note" role="status">
                  {succesOctroi}
                </p>
              ) : null}
              <PlusDeResultats
                cursor={page.cursor}
                chargement={page.chargement}
                onClick={() => void page.plus()}
              />
            </>
          )
        }
      </Async>
    </div>
  );
}

/* ========================================================================== */
/* Onglet — Affectations                                                      */
/* ========================================================================== */

/**
 * LOT 3.4 — l'affectation d'un élève à un tuteur, depuis la console.
 *
 * `CON-ADM-CRE-02` et `CON-ADM-CRE-03`, tous deux BLOQUANTS : « l'onglet
 * "Création" ne contient que trois formulaires […] Aucun sélecteur de tuteur,
 * aucun bouton d'affectation, nulle part. LA CAPACITÉ EXISTE POURTANT, et nous
 * l'avons exercée HORS INTERFACE pour établir le fait. »
 *
 * Les trois routes répondaient depuis le sprint B7. Ce qui manquait était
 * exactement l'écran — et son absence bouclait un cul-de-sac : un tuteur créé
 * depuis la console atterrissait sur « Aucun élève ne vous est affecté · Un
 * administrateur de l'organisation rattache… », c'est-à-dire renvoyé à la
 * personne assise devant CET écran, qui n'avait pas l'outil.
 *
 * ── Deux pièges du contrat, tenus ici ──────────────────────────────────────
 *
 * 1. UN RETRAIT NE SUPPRIME RIEN. Il date `fin`. Savoir qui a suivi un mineur,
 *    et entre quelles dates, est une exigence de conformité (§10.8). L'écran
 *    le DIT au lieu de le taire, et propose de voir l'historique.
 * 2. RÉAFFECTER APRÈS UN RETRAIT DOIT MARCHER. L'unicité `(tuteurId, eleveId)`
 *    a été délibérément abaissée à un simple index pour cela : une seconde
 *    affectation du même couple crée une NOUVELLE ligne, la première restant
 *    close et lisible. Cet écran n'introduit donc aucun `upsert` sur ce
 *    couple ; il appelle `POST`, que le serveur rend idempotent en ne
 *    recréant rien tant qu'une affectation ACTIVE existe.
 */
function AffectationsTab() {
  const { user } = useAuth();
  const orgId = user?.tenantId ?? null;

  const chargerAffectations = useCallback(
    () => (orgId ? api.listAffectations(orgId, true) : Promise.resolve({ affectations: [] })),
    [orgId],
  );
  const affectations = useAsync<ListeAffectationsResponse>(chargerAffectations);

  /* Un sélecteur, pas la liste des comptes : rien n'y est masqué (D-7). */
  const chargerTuteurs = useCallback(
    () => api.listComptes({ role: 'tuteur', inclureTests: true }),
    [],
  );
  const tuteurs = useAsync<ListeComptesResponse>(chargerTuteurs);

  const chargerEleves = useCallback(() => api.listElevesAdmin(), []);
  const eleves = useAsync<ListeElevesAdminResponse>(chargerEleves);

  const [tuteurId, setTuteurId] = useState('');
  const [eleveId, setEleveId] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [succes, setSucces] = useState<string | null>(null);

  /**
   * Le `tenantId` d'un admin vaut son `orgId`, ou son propre identifiant quand
   * il n'appartient à aucune organisation. Dans ce second cas les routes
   * répondraient « Organisation introuvable » : on le DIT, plutôt que de
   * laisser l'écran échouer en boucle sur un message de chargement.
   */
  const sansOrganisation = orgId !== null && orgId === user?.id;

  async function affecter(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!orgId || !tuteurId || !eleveId) return;
    setEnvoi(true);
    setErreur(null);
    setSucces(null);
    try {
      const res = await api.creerAffectation(orgId, { tuteurId, eleveId });
      setSucces(
        `${res.affectation.elevePrenom} est maintenant suivi par ${
          res.affectation.tuteurNom ?? res.affectation.tuteurEmail
        }.`,
      );
      setEleveId('');
      affectations.reload();
    } catch (err: unknown) {
      setErreur(messageErreur(err));
    } finally {
      setEnvoi(false);
    }
  }

  async function retirer(affectationId: string, prenom: string) {
    if (!orgId) return;
    setErreur(null);
    setSucces(null);
    try {
      await api.retirerAffectation(orgId, affectationId);
      setSucces(
        `L'affectation de ${prenom} est retirée. Le tuteur perd l'accès immédiatement ; la ligne reste datée dans l'historique.`,
      );
      affectations.reload();
    } catch (err: unknown) {
      setErreur(messageErreur(err));
    }
  }

  if (sansOrganisation) {
    return (
      <div className="suivi-stack">
        <div className="suivi-section-header">
          <h2>Affectations</h2>
        </div>
        <EmptyState title="Votre compte n'est rattaché à aucune organisation">
          Les affectations appartiennent à une organisation. Votre compte d'administration n'en a
          pas, et les routes d'affectation répondraient « Organisation introuvable ». C'est un
          rattachement à faire en base, pas une manipulation d'écran.
        </EmptyState>
      </div>
    );
  }

  return (
    <div className="suivi-stack">
      <div className="suivi-section-header">
        <h2>Affectations</h2>
      </div>

      <section className="card">
        <h3>Affecter un élève à un professeur</h3>
        <p className="card-sub">
          Le tuteur voit alors la fiche de cet élève, sa progression et ses évaluations à valider.
          Il ne voit rien des autres élèves.
        </p>

        <form className="form-grid" onSubmit={(e) => void affecter(e)}>
          <div className="field">
            <label htmlFor="affectation-tuteur">Professeur</label>
            <Async resource={tuteurs}>
              {(data) => (
                <select
                  id="affectation-tuteur"
                  value={tuteurId}
                  onChange={(e) => setTuteurId(e.target.value)}
                  required
                >
                  <option value="">Choisir un professeur…</option>
                  {data.comptes.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.prenom} {c.nom} · {c.email}
                    </option>
                  ))}
                </select>
              )}
            </Async>
          </div>

          <div className="field">
            <label htmlFor="affectation-eleve">Élève</label>
            <Async resource={eleves}>
              {(data) => (
                <select
                  id="affectation-eleve"
                  value={eleveId}
                  onChange={(e) => setEleveId(e.target.value)}
                  required
                >
                  <option value="">Choisir un élève…</option>
                  {data.eleves.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.prenom} · {niveauScolaireLabel(e.niveauScolaire)} · {e.parentEmail}
                    </option>
                  ))}
                </select>
              )}
            </Async>
          </div>

          <div className="row">
            <button
              type="submit"
              className="btn btn--primary"
              disabled={envoi || !tuteurId || !eleveId}
            >
              {envoi ? 'Affectation…' : 'Affecter'}
            </button>
          </div>
        </form>

        {erreur ? <ErreurChargement message={erreur} /> : null}
        {succes ? (
          <p className="suivi-note" role="status">
            {succes}
          </p>
        ) : null}
      </section>

      <section className="card">
        <h3>Affectations actives</h3>
        <p className="card-sub">
          Retirer une affectation ferme l'accès du tuteur immédiatement. La ligne est datée, pas
          supprimée.
        </p>
        <Async resource={affectations}>
          {(data) =>
            data.affectations.length === 0 ? (
              <EmptyState title="Aucune affectation active">
                Aucun tuteur ne suit d'élève pour le moment. Tant que c'est le cas, les consoles
                tuteur sont vides — et c'est exact, pas cassé.
              </EmptyState>
            ) : (
              <div className="table-wrap">
                <table className="data-table">
                  <caption className="sr-only">
                    Affectations actives de l'organisation : tuteur, élève, date de début
                  </caption>
                  <thead>
                    <tr>
                      <th scope="col">Professeur</th>
                      <th scope="col">Élève</th>
                      <th scope="col">Depuis</th>
                      <th scope="col">
                        <span className="sr-only">Action</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.affectations.map((a) => (
                      <tr key={a.id}>
                        <th scope="row">
                          <div style={{ fontWeight: 600 }}>{a.tuteurNom ?? a.tuteurEmail}</div>
                          <div className="cell-sub">{a.tuteurEmail}</div>
                        </th>
                        <td>{a.elevePrenom}</td>
                        <td className="cell-sub">{formatDateHeure(a.debut)}</td>
                        <td>
                          <button
                            type="button"
                            className="btn btn--ghost btn--sm"
                            onClick={() => void retirer(a.id, a.elevePrenom)}
                          >
                            Retirer
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          }
        </Async>
      </section>
    </div>
  );
}

/* ========================================================================== */
/* Onglet — Création                                                          */
/* ========================================================================== */

interface ChampsCompte {
  email: string;
  password: string;
  prenom: string;
  nom: string;
}

const COMPTE_VIDE: ChampsCompte = { email: '', password: '', prenom: '', nom: '' };

/**
 * Formulaire de création d'un compte titulaire. La route appelée porte le
 * rôle (`POST /tuteurs` ou `POST /parents`) : le corps est identique.
 * Les règles de validation sont celles de l'API (courriel valide, mot de
 * passe ≥ 8 caractères) — rappelées ici, jamais réimplémentées.
 */
function FormulaireCompte({
  id,
  titre,
  sousTitre,
  soumettre,
  onCree,
}: {
  /** Préfixe des `id` de champs — un slug stable, jamais le titre affiché
      (qui contient des espaces et des accents, interdits dans un `id`). */
  id: string;
  titre: string;
  sousTitre: string;
  soumettre: (input: ChampsCompte) => Promise<{ email: string }>;
  onCree: () => void;
}) {
  const [champs, setChamps] = useState<ChampsCompte>(COMPTE_VIDE);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [succes, setSucces] = useState<string | null>(null);

  const set = (cle: keyof ChampsCompte) => (v: string) =>
    setChamps((prev) => ({ ...prev, [cle]: v }));

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setEnvoi(true);
    setErreur(null);
    setSucces(null);
    try {
      const cree = await soumettre(champs);
      setSucces(`Compte ${cree.email} créé.`);
      setChamps(COMPTE_VIDE);
      onCree();
    } catch (err) {
      setErreur(messageErreur(err));
    } finally {
      setEnvoi(false);
    }
  }

  return (
    <form className="card" onSubmit={onSubmit} noValidate>
      <h3>{titre}</h3>
      <p className="card-sub">{sousTitre}</p>

      {erreur ? (
        <p className="callout callout--rare" role="alert">
          {erreur}
        </p>
      ) : null}
      {succes ? (
        <p className="callout callout--ok" role="status">
          {succes}
        </p>
      ) : null}

      <div className="form-grid" style={{ marginTop: 'var(--espace-4)' }}>
        <span className="field">
          <label htmlFor={`${id}-prenom`}>Prénom</label>
          <input
            id={`${id}-prenom`}
            className="control"
            required
            value={champs.prenom}
            onChange={(e) => set('prenom')(e.target.value)}
          />
        </span>
        <span className="field">
          <label htmlFor={`${id}-nom`}>Nom</label>
          <input
            id={`${id}-nom`}
            className="control"
            required
            value={champs.nom}
            onChange={(e) => set('nom')(e.target.value)}
          />
        </span>
      </div>

      <span className="field">
        <label htmlFor={`${id}-email`}>Adresse courriel</label>
        <input
          id={`${id}-email`}
          className="control"
          type="email"
          autoComplete="off"
          required
          value={champs.email}
          onChange={(e) => set('email')(e.target.value)}
        />
      </span>

      <span className="field">
        <label htmlFor={`${id}-password`}>Mot de passe provisoire</label>
        <input
          id={`${id}-password`}
          className="control"
          type="password"
          autoComplete="new-password"
          required
          minLength={8}
          value={champs.password}
          onChange={(e) => set('password')(e.target.value)}
        />
        <span className="hint">8 caractères minimum. À communiquer hors de la console.</span>
      </span>

      <button type="submit" className="btn btn--sm" disabled={envoi}>
        {envoi ? 'Création…' : titre}
      </button>
    </form>
  );
}

/** Formulaire d'ajout d'un enfant à un parent existant. */
function FormulaireEnfant({ parents }: { parents: readonly CompteListeItemDto[] }) {
  const [parentId, setParentId] = useState('');
  const [prenom, setPrenom] = useState('');
  // Sprint 21 — MÊME contrat que les deux parcours d'inscription de l'app
  // mobile (`AddChildRequest`) : date civile `AAAA-MM-JJ` choisie dans un
  // calendrier (`<input type="date">`, bornes de `@silex/shared`), genre
  // facultatif dont le refus de répondre est une valeur à part entière.
  const [dateNaissance, setDateNaissance] = useState('');
  const [genre, setGenre] = useState<Genre | ''>('');
  const [niveau, setNiveau] = useState<NiveauScolaire>('sec1');
  const [matieres, setMatieres] = useState<readonly Matiere[]>([]);
  const bornes = bornesDateNaissance();
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [succes, setSucces] = useState<string | null>(null);

  function bascule(m: Matiere) {
    setMatieres((prev) => (prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]));
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setEnvoi(true);
    setErreur(null);
    setSucces(null);
    try {
      const enfant = await api.creerEnfant({
        parentId,
        prenom,
        dateNaissance,
        // Omis quand la question n'a pas reçu de réponse : `null` en base
        // (« sans réponse ») ne se confond pas avec `prefere_ne_pas_repondre`.
        ...(genre === '' ? {} : { genre }),
        niveauScolaire: niveau,
        ...(matieres.length > 0 ? { matieres } : {}),
      });
      setSucces(`Profil de ${enfant.prenom} créé.`);
      setPrenom('');
      setDateNaissance('');
      setGenre('');
      setMatieres([]);
    } catch (err) {
      setErreur(messageErreur(err));
    } finally {
      setEnvoi(false);
    }
  }

  return (
    <form className="card" onSubmit={onSubmit} noValidate>
      <h3>Créer un enfant</h3>
      <p className="card-sub">Rattaché au compte parent choisi ci-dessous.</p>

      {erreur ? (
        <p className="callout callout--rare" role="alert">
          {erreur}
        </p>
      ) : null}
      {succes ? (
        <p className="callout callout--ok" role="status">
          {succes}
        </p>
      ) : null}

      {parents.length === 0 ? (
        <p className="callout callout--calm" style={{ marginTop: 'var(--espace-4)' }}>
          Aucun compte parent n'existe encore. Créez d'abord un parent : un enfant est toujours
          rattaché à un parent.
        </p>
      ) : null}

      <span className="field" style={{ marginTop: 'var(--espace-4)' }}>
        <label htmlFor="enfant-parent">Parent</label>
        <select
          id="enfant-parent"
          className="control"
          required
          disabled={parents.length === 0}
          value={parentId}
          onChange={(e) => setParentId(e.target.value)}
        >
          <option value="">Choisir un parent…</option>
          {parents.map((p) => (
            <option key={p.id} value={p.id}>
              {p.prenom} {p.nom} — {p.email}
            </option>
          ))}
        </select>
      </span>

      <div className="form-grid">
        <span className="field">
          <label htmlFor="enfant-prenom">Prénom de l'enfant</label>
          <input
            id="enfant-prenom"
            className="control"
            required
            value={prenom}
            onChange={(e) => setPrenom(e.target.value)}
          />
        </span>
        <span className="field">
          <label htmlFor="enfant-naissance">Date de naissance</label>
          <input
            id="enfant-naissance"
            className="control"
            type="date"
            required
            min={bornes.min}
            max={bornes.max}
            value={dateNaissance}
            onChange={(e) => setDateNaissance(e.target.value)}
          />
          <span className="hint">
            Elle décide qui donne le consentement : le parent en dessous de 14 ans, l'élève lui-même
            au-delà.
          </span>
        </span>
        <span className="field">
          <label htmlFor="enfant-genre">Genre (facultatif)</label>
          <select
            id="enfant-genre"
            className="control"
            value={genre}
            onChange={(e) => setGenre(e.target.value as Genre | '')}
          >
            <option value="">Sans réponse</option>
            {GENRES.map((g) => (
              <option key={g} value={g}>
                {GENRE_LIBELLE[g]}
              </option>
            ))}
          </select>
          <span className="hint">
            Jamais exigé. « Préfère ne pas répondre » et l'absence de réponse sont stockées
            différemment.
          </span>
        </span>
        <span className="field">
          <label htmlFor="enfant-niveau">Niveau scolaire</label>
          <select
            id="enfant-niveau"
            className="control"
            value={niveau}
            onChange={(e) => setNiveau(e.target.value as NiveauScolaire)}
          >
            {NIVEAUX_SCOLAIRES.map((n) => (
              <option key={n} value={n}>
                {niveauScolaireLabel(n)}
              </option>
            ))}
          </select>
        </span>
      </div>

      <span className="field">
        <span className="lbl">Matières suivies</span>
        <span className="row wrap">
          {MATIERES.map((m) => (
            <label key={m} className="check">
              <input type="checkbox" checked={matieres.includes(m)} onChange={() => bascule(m)} />
              {matiereLabel(m)}
            </label>
          ))}
        </span>
        <span className="hint">Optionnel — modifiable ensuite depuis le compte parent.</span>
      </span>

      <button
        type="submit"
        className="btn btn--sm"
        disabled={envoi || parents.length === 0 || !dateNaissanceValide(dateNaissance)}
      >
        {envoi ? 'Création…' : 'Créer un enfant'}
      </button>
    </form>
  );
}

function CreationTab() {
  // La liste des parents alimente le sélecteur du formulaire enfant : l'admin
  // choisit un parent existant plutôt que de saisir un identifiant opaque.
  // Un sélecteur : les parents de test y restent choisissables (D-7).
  const loader = useCallback(() => api.listComptes({ role: 'parent', inclureTests: true }), []);
  const parents = useAsync<ListeComptesResponse>(loader);
  const { reload } = parents;

  return (
    <div className="suivi-stack">
      <div className="suivi-section-header">
        <h2>Création de comptes</h2>
      </div>

      <div className="admin-grid admin-grid--split">
        <div className="stack">
          <FormulaireCompte
            id="tuteur"
            titre="Créer un professeur"
            /* LOT 3.5 (CON-TUT-LOG-04) — cette phrase promettait que
               « l'authentification à deux facteurs est exigée à la première
               connexion ». La campagne a créé un tuteur par cet écran puis
               ouvert sa toute première session : `POST /v1/auth/login` a rendu
               les deux jetons sans aucune étape de second facteur.

               La promesse était fausse depuis l'assouplissement du 2026-08-30 :
               `ROLES_MFA_OBLIGATOIRE_PAR_DEFAUT` est vide, décision produit du
               fondateur pour la phase de démonstration
               (`auth.service.ts`). L'obligation revient en posant
               `MFA_ROLES_OBLIGATOIRE=tuteur,admin` dans l'environnement, sans
               toucher au code.

               La console ne lit pas la configuration du serveur : elle ne peut
               donc affirmer NI que le second facteur est exigé, NI qu'il ne
               l'est pas. Elle dit ce qui est vrai dans les deux cas. */
            sousTitre="Accès à la console. Le second facteur n'est pas imposé à la création : le professeur l'active depuis son compte, et l'obligation par rôle se règle côté serveur."
            soumettre={(input) => api.creerTuteur(input)}
            onCree={reload}
          />
          <FormulaireCompte
            id="parent"
            titre="Créer un parent"
            sousTitre="Accès à l'application mobile et au tableau de bord parent."
            soumettre={(input) => api.creerParent(input)}
            onCree={reload}
          />
        </div>

        <div className="stack">
          {parents.status === 'error' ? (
            <ErreurChargement message={parents.message} />
          ) : (
            <FormulaireEnfant parents={parents.status === 'ready' ? parents.data.comptes : []} />
          )}
        </div>
      </div>
    </div>
  );
}

/* ========================================================================== */
/* Écran                                                                      */
/* ========================================================================== */

type TabId =
  'consommation' | 'connexions' | 'comptes' | 'eleves' | 'affectations' | 'creation' | 'reglages';

const TABS: readonly { readonly id: TabId; readonly label: string }[] = [
  { id: 'consommation', label: 'Consommation IA' },
  { id: 'connexions', label: 'Connexions' },
  { id: 'comptes', label: 'Comptes' },
  { id: 'eleves', label: 'Élèves' },
  { id: 'affectations', label: 'Affectations' },
  { id: 'creation', label: 'Création' },
];

/**
 * LES DEUX ENTRÉES DU MENU PAR TÂCHES QUI REGROUPENT L'ADMINISTRATION —
 * refonte de la console, L1 (15 octobre 2026).
 *
 *  - « Comptes » (`/comptes/:onglet`) : les comptes, les élèves, les
 *    affectations, la création. Le réglage du manuel n'y est plus déroulé
 *    sous la liste (c'est lui qui la rendait haute de 24 écrans) : il est
 *    sous « Plateforme › Réglages ».
 *  - « Plateforme » (`/plateforme/:onglet`) : la consommation du modèle, les
 *    connexions, les réglages.
 *
 * `/administration/:onglet` reste servie À L'IDENTIQUE (aucune adresse retirée).
 */
export type SectionAdministration = 'comptes' | 'plateforme';

interface CadreSection {
  readonly titre: string;
  readonly sous: string;
  readonly onglets: readonly { readonly id: TabId; readonly label: string }[];
  readonly defaut: TabId;
}

const SECTIONS: Readonly<Record<SectionAdministration, CadreSection>> = {
  comptes: {
    titre: 'Comptes',
    sous: 'Les comptes, les élèves, qui suit qui, et la création d’un compte.',
    onglets: [
      { id: 'comptes', label: 'Comptes' },
      { id: 'eleves', label: 'Élèves' },
      { id: 'affectations', label: 'Affectations' },
      { id: 'creation', label: 'Création' },
    ],
    defaut: 'comptes',
  },
  plateforme: {
    titre: 'Plateforme',
    sous: 'Ce que coûte le modèle, qui se connecte, et les réglages du produit.',
    onglets: [
      { id: 'consommation', label: 'Consommation IA' },
      { id: 'connexions', label: 'Connexions' },
      ...(MANUEL_VIVANT ? [{ id: 'reglages' as const, label: 'Réglages' }] : []),
    ],
    defaut: 'consommation',
  },
};

/**
 * Administration et observabilité (PRD Module 15, Sprint 14).
 *
 * Branché sur `/v1/admin/*`, réservé au rôle `admin` côté API
 * (`requireRole(['admin'])`) — la garde `RequireAuth adminOnly` de `App.tsx`
 * n'est qu'un confort d'affichage, jamais la frontière de sécurité.
 *
 * Sans `section` : l'écran d'avant, `/administration/:onglet`, six onglets.
 */
export function AdministrationPage({ section }: { readonly section?: SectionAdministration }) {
  /**
   * L'ONGLET VIT DANS L'URL, PAS DANS UN ÉTAT LOCAL.
   *
   * Le lien se partage (`/administration/affectations`), le retour du
   * navigateur fonctionne, et un rechargement ne perd rien.
   *
   * L'écran ouvre sur « Comptes » et non sur la consommation IA : c'est
   * l'onglet dont on a besoin neuf fois sur dix, et l'ancien défaut obligeait
   * à deux clics à chaque venue.
   */
  const { onglet } = useParams<{ onglet?: string }>();
  const navigate = useNavigate();
  const cadre = section === undefined ? null : SECTIONS[section];
  const onglets = cadre?.onglets ?? TABS;
  const base = section === undefined ? '/administration' : `/${section}`;
  const active: TabId = onglets.find((t) => t.id === onglet)?.id ?? cadre?.defaut ?? 'comptes';
  const setActive = (id: TabId): void => navigate(`${base}/${id}`);

  // Le contenu des onglets est monté à la demande : chaque onglet fait ses
  // propres appels, on n'interroge pas les 5 vues au chargement de l'écran.
  const contenu = useMemo(() => {
    switch (active) {
      case 'consommation':
        return <ConsommationTab />;
      case 'connexions':
        return <ConnexionsTab />;
      case 'comptes':
        return <ComptesTab avecReglage={section === undefined} />;
      case 'eleves':
        return <ElevesTab />;
      case 'affectations':
        return <AffectationsTab />;
      case 'creation':
        return <CreationTab />;
      case 'reglages':
        return MANUEL_VIVANT ? <ReglageManuelVivant /> : null;
      default:
        return null;
    }
  }, [active, section]);

  return (
    <div className="content__inner">
      <div className="page-head">
        <div>
          <h1>{cadre?.titre ?? 'Administration'}</h1>
          <p className="page-sub">
            {cadre?.sous ??
              'Création de comptes, activité de connexion et consommation IA de la plateforme.'}
          </p>
        </div>
      </div>

      <div
        className="tabs"
        role="tablist"
        aria-label={cadre ? `Sections — ${cadre.titre}` : "Sections de l'administration"}
      >
        {onglets.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={tab.id === active}
            className={tab.id === active ? 'tab is-active' : 'tab'}
            onClick={() => setActive(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {contenu}
    </div>
  );
}
